import React, { useContext, useEffect, useState } from 'react';
import {
    compactTableInputStyle,
    inputStyle,
    inputweekStyle,
    labelStyle,
    requiredPanelStyle,
    statusInputStyle
} from '../CampaignBrief/CampaignBriefStyles';
import CompactTable from '../../generic/CompactTable/CompactTableNew';
import { CampaignLookup } from '../../../services/CampaignsDataService';
import AsTextWithStatusAndSearch from '../../generic/AsTextWithStatusAndSearch';
import { AsDatePicker, AsNumber } from '@adserve/adserve-react-components';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { BusinessTypes } from '../../../constants/Enums';
import CampaignCompactTableColumnDefinitions from '../CampaignBrief/ColumnDefinitions/CampaignCompactTableColumnDefinitions';
import moment from 'moment';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import { DefaultInputStatus } from '../CampaignBrief/CampaignBriefUtils';

const CampaignBurstSummary = ({
    campaignBurst,
    updateCampaignBurst,
    parentCampaign,
    changeBurstName,
    focusBurstName,
    readOnly,
    campaignBurstNameStatus,
    setCampaignBurstNameStatus
}) => {
    const { dateFormat, formatNumber } = useContext(SystemSettingsContext);
    const { showInvalidatePlanMessage } = useContext(AirtimePlanningContext);
    const [minDate, setMinDate] = useState(moment().utc(true).startOf('day'));
    const [maxDate, setMaxDate] = useState(moment().utc(true).startOf('day'));

    useEffect(() => {
        let min = moment().utc(true).startOf('day');

        // min = min.add(1, 'day');
        const parentCampaignStartDate = moment(parentCampaign?.startDate).utc(true).startOf('day');
        const parentCampaignEndDate = moment(parentCampaign?.endDate).utc(true).startOf('day');

        if (min.isBefore(parentCampaignStartDate)) {
            min = parentCampaignStartDate.clone();
        }
        setMinDate(min);
        setMaxDate(parentCampaignEndDate);
    }, [parentCampaign]);

    useEffect(() => {
        const startDate = minDate.clone();
        const endDate = minDate.clone();
        if (!campaignBurst.startDate && !campaignBurst.endDate) {
            endDate.add(1, 'week');
            updateCampaignBurst('startDate', startDate);
            updateCampaignBurst('endDate', endDate);
        }

        if (campaignBurst) {
            const cmpStartDate = moment(campaignBurst.startDate).utc(true).startOf('day');
            const cmpEndDate = moment(campaignBurst.endDate).utc(true).startOf('day');
            let weeks = 0;
             
            if (campaignBurst.weeks !== null || (campaignBurst.startDate && campaignBurst.endDate)) {
                weeks = cmpEndDate.diff(cmpStartDate, 'week') + 1;
                campaignBurst.weeks = weeks;
            }
        }
    }, [campaignBurst]);

    const handleCampaignChange = (event) => {
        updateCampaignBurst('campaignName', event.target.value);
    };

    const handleCampaignDropdownClick = (searchResult) => {
        updateCampaignBurst('campaignId', searchResult.id);
        updateCampaignBurst('campaignName', searchResult.name);
    };

    const handleNameChange = (event) => {
        const { name, value } = event.target;
        setCampaignBurstNameStatus(DefaultInputStatus());
        changeBurstName(name, value);
    };

    const handleStartDateChange = (event) => {
        const value = event.target.value;

        if (campaignBurst.endDate && value > new Date(campaignBurst.endDate)) {
            campaignBurst.startDate = campaignBurst.endDate;
            updateCampaignBurst('startDate', value);
            updateCampaignBurst('endDate', value);
        } else {
            updateCampaignBurst('startDate', value);
        }

        showInvalidatePlanMessage(event.target, false);
    };

    const handleEndDateChange = (event) => {
        const value = event.target.value;
        updateCampaignBurst('endDate', value);

        showInvalidatePlanMessage(event.target, false);
    };

    const handleWeeksChange = (event) => {
        const numWeeks = event.target.value;

        if (!numWeeks) {
            updateCampaignBurst('endDate', null);
            updateCampaignBurst('weeks', null);
            campaignBurst.weeks = null;
            return;
        }

        if (numWeeks === campaignBurst.weeks || numWeeks < 1) {
            return;
        }

        const startDate = new Date(campaignBurst.startDate);
        const lastDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 7 * event.target.value);
        const endDate = new Date(lastDate - 1);
        updateCampaignBurst('endDate', endDate);
        updateCampaignBurst('weeks', numWeeks);

        showInvalidatePlanMessage(event.target); 
    };  

    return (
        <div style={{ ...requiredPanelStyle, paddingBottom: 20, display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 15, fontWeight: 500, marginBottom: 10 }}>
                {window.translate('Burst Summary *')}
            </span>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ width: '20%' }}>
                    <label style={labelStyle}>{window.translate('Parent campaign name')}</label>
                    <CompactTable
                        id='parent-campaign'
                        inputStyle={compactTableInputStyle}
                        value={campaignBurst?.campaignName ?? parentCampaign?.name}
                        onChange={handleCampaignChange}
                        searchFunction={(searchTerm, params) =>
                            CampaignLookup(searchTerm, BusinessTypes.SANDP.name, params)
                        }
                        tableColumnDefinitions={CampaignCompactTableColumnDefinitions}
                        onDropdownClick={handleCampaignDropdownClick}
                        disabled={readOnly}
                    />
                </div>
                <div style={{ width: '20%', marginLeft: '4%' }}>
                    <AsTextWithStatusAndSearch
                        id='burst-name'
                        name='name'
                        title={window.translate('Burst name *')}
                        labelStyle={labelStyle}
                        inputStyle={statusInputStyle}
                        value={campaignBurst?.name}
                        disabled={readOnly}
                        onChange={handleNameChange}
                        onFocus={() => focusBurstName(true)}
                        searchStatus={campaignBurstNameStatus}
                        numResultsInDropdown={0}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', width: '50%' }}>
                    <div style={{ width: '40%' }}>
                        <label style={labelStyle}>{window.translate('Burst start date')}</label>
                        <AsDatePicker
                            id='burst-startdate'
                            inputStyle={{ ...inputStyle, width: '80%' }}
                            currentDate={campaignBurst?.startDate || 'Select date ...'}
                            onChange={handleStartDateChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            handleMultiple={true}
                            disabled={readOnly}
                            dateFormat={dateFormat}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '12%' }}>
                        <AsNumber
                            id='burst-weeks'
                            name='weeks'
                            title={window.translate('Weeks')}
                            labelStyle={labelStyle}
                            inputStyle={{ ...inputweekStyle }}
                            value={campaignBurst?.weeks}
                            onChange={handleWeeksChange}
                            disabled={readOnly}
                            allowFloat={false}
                            formatNumber={formatNumber}
                            digitLimit={5}
                        />
                    </div>
                    <div style={{ width: '40%' }}>
                        <label style={labelStyle}>{window.translate('Burst end date')}</label>
                        <AsDatePicker
                            id='burst-enddate'
                            inputStyle={{ ...inputStyle, width: '80%' }}
                            currentDate={campaignBurst?.endDate || 'Select date ...'}
                            onChange={handleEndDateChange}
                            minDate={minDate}
                            maxDate={maxDate}
                            handleMultiple={true}
                            disabled={readOnly}
                            dateFormat={dateFormat}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignBurstSummary;
